import {
  Box, Typography, Card, CardContent, Stack, Chip, CircularProgress,
  Grid, Divider,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Map as MapIcon, Visibility, AccessTime, LocalShipping } from '@mui/icons-material'
import { useDevices } from '../../hooks/useDevices'
import { useAuthStore } from '../../store/authStore'

function unwrap<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d as T[]
  const o = d as { devices?: T[] } | undefined
  return o?.devices ?? []
}

interface Device {
  id: string
  name: string
  device_identifier: string
  status: string
  last_seen_at?: string
}

/**
 * The viewer is a read-only role. They can see trips/last activity for the
 * specific devices a tenant admin has assigned to them — nothing else.
 */
export default function ViewerDashboardPage() {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const { data, isLoading } = useDevices()
  const devices = unwrap<Device>(data)

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress sx={{ color: '#34A8E0' }} />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: 2, bgcolor: '#eff6ff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Visibility sx={{ color: '#3b82f6' }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em' }}>
            {t('viewer.title')} · {user?.name}
          </Typography>
          <Typography variant="body2" color="#94a3b8" sx={{ mt: 0.25 }}>
            {t('viewer.assignedDevices', { count: devices.length })}
          </Typography>
        </Box>
      </Box>

      {devices.length === 0 ? (
        <Card sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 6, textAlign: 'center' }}>
          <LocalShipping sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
          <Typography variant="body1" color="#64748b" fontWeight={600}>
            {t('viewer.noDevicesShared')}
          </Typography>
          <Typography variant="caption" color="#94a3b8" sx={{ mt: 0.5 }}>
            {t('viewer.contactAdmin')}
          </Typography>
        </Card>
      ) : (
        <Grid container spacing={2.5}>
          {devices.map((d) => (
            <Grid key={d.id} item xs={12} md={6} lg={4}>
              <Card sx={{ border: '1px solid #e2e8f0', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{
                        width: 42, height: 42, borderRadius: 2, bgcolor: '#EBF6FD',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <MapIcon sx={{ color: '#34A8E0' }} />
                      </Box>
                      <Box>
                        <Typography variant="body1" fontWeight={700} color="#1e293b">{d.name}</Typography>
                        <Typography variant="caption" color="#94a3b8">{d.device_identifier}</Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={d.status}
                      size="small"
                      sx={{
                        fontSize: 10, fontWeight: 600, textTransform: 'capitalize',
                        bgcolor: d.status === 'online' ? '#f0fdf4' : '#f8fafc',
                        color: d.status === 'online' ? '#10b981' : '#94a3b8',
                        border: `1px solid ${d.status === 'online' ? '#bbf7d0' : '#e2e8f0'}`,
                      }}
                    />
                  </Box>

                  <Divider sx={{ my: 2, borderColor: '#f1f5f9' }} />

                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTime sx={{ fontSize: 14, color: '#94a3b8' }} />
                      <Typography variant="caption" color="#64748b">
                        {t('viewer.lastActivity')}: {d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : t('viewer.never')}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  )
}
